import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import ReusableButton from './ReusableButton';
import * as precheckService from '../../services/precheckService';
import type { Precheck, PrecheckPos } from '../../services/precheckService';

interface MerchantSubmissionsTableProps {
  merchantPrecheckPoses: PrecheckPos[];
  merchantPrechecks: Precheck[];
}

const MerchantSubmissionsTable: React.FC<MerchantSubmissionsTableProps> = ({
  merchantPrecheckPoses,
  merchantPrechecks,
}) => {
  const { t } = useTranslation();

  const combinedSubmissions = [
    ...merchantPrecheckPoses.map((p) => ({
      id: p.id,
      company: p.company,
      type: 'POS' as const,
      precheckType: 'precheck-pos' as const,
      createdAt: p.createdAt,
    })),
    ...merchantPrechecks.map((p) => ({
      id: p.id,
      company: p.genInfo.genComp,
      type: 'ECOM' as const,
      precheckType: 'precheck' as const,
      createdAt: p.createdAt,
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const handleViewPdf = async (type: 'precheck' | 'precheck-pos', id: number) => {
    try {
      const blob = await precheckService.getPdf(type, id, 'en', false);
      precheckService.openPdfInNewTab(blob);
    } catch (error) {
      console.error('Error fetching PDF:', error);
    }
  };

  if (combinedSubmissions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200">
        <h2 className="text-xl font-semibold text-slate-900">{t('dashboard.yourSubmissions')}</h2>
        <p className="text-slate-600 text-sm mt-1">{t('dashboard.viewYourPrechecks')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="border px-6 py-3 text-left">{t('merchant.company')}</th>
              <th className="border px-6 py-3 text-left">{t('common.type')}</th>
              <th className="border px-6 py-3 text-left">{t('common.submitted')}</th>
              <th className="border px-6 py-3 text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {combinedSubmissions.map((item) => (
              <tr key={`${item.type}-${item.id}`} className="hover:bg-slate-50">
                <td className="border px-6 py-3">{item.company}</td>
                <td className="border px-6 py-3">{item.type}</td>
                <td className="border px-6 py-3">{new Date(item.createdAt).toLocaleDateString()}</td>
                <td className="border px-6 py-3 text-center">
                  <ReusableButton
                    label={t('common.view')}
                    onClick={() => handleViewPdf(item.precheckType, item.id)}
                    icon={<BookOpenIcon className="size-4" />}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MerchantSubmissionsTable;
